<div class="openstreetmap-container">
  <div #mapContainer class="map-element" [style.height]="height" [class.drawing-mode]="drawingMode">
    @if (!isMapInitialized) {
      <div class="map-loading">
        <div class="loading-spinner"></div>
        <p>Loading map...</p>
      </div>
    }
  </div>

  <!-- Data Loading Indicator (Top Center) -->
  @if (isLoading) {
    <div class="map-data-loading">
      <div class="loading-spinner-small"></div>
      <span>{{ 'Loading' | translate }}</span>
    </div>
  }

  <!-- Map Drawing Controls (Top Right) -->
  @if (mapOptions.showDrawingControls) {
    <app-osm-drawing-toolbar
      [direction]="(mapOptions.drawingControlsDirection || 'row')"
      [visibilityManager]="visibilityManager"
      [distanceActive]="isDistanceMeasurementMode()"
      [rectangleActive]="isDrawingRectangle()"
      [polygonActive]="isDrawingPolygon()"
      [boxPolygonActive]="isDrawingBoxPolygon()"
      [customPolygonActive]="isDrawingCustomPolygon()"
      (toggleDistance)="toggleDistanceMeasurement()"
      (enableRectangle)="enableRectangleDrawing()"
      (enablePolygon)="enablePolygonDrawing()"
      (enableBoxPolygon)="enableBoxPolygonDrawing()"
      (enableCustomPolygon)="enableCustomPolygonDrawing()"
      (clearAllDrawings)="clearAllDrawnShapes()"
      (openGeoUpload)="osmIndexOverlay.openGeoFileUploadModalCombined()"
    ></app-osm-drawing-toolbar>
  }

  @if (
    mapOptions.indexOpacityToggle
    && (
      osmIndexOverlay.hasActiveIndexLayer()
      || currentSatelliteLayer === 'sentinel'
      || currentSatelliteLayer === 'landsat'
    )
  ) {
    <div class="map-opacity-control">
      @if (
        currentSatelliteLayer === 'sentinel' || currentSatelliteLayer === 'landsat'
      ) {
        <div class="opacity-control-container" [title]="currentSatelliteLabel">
          <span class="opacity-icon">
            <i class="pi pi-map"></i>
          </span>
          <input
            type="range"
            min="0"
            max="100"
            [(ngModel)]="satelliteLayerOpacity"
            (ngModelChange)="setSatelliteLayerOpacityValue($event)"
            class="opacity-slider">
          <span class="opacity-value">{{ satelliteLayerOpacity }}%</span>
        </div>
      }

      @if (osmIndexOverlay.hasActiveIndexLayer()) {
        <div class="opacity-control-container" [title]="osmIndexOverlay.getActiveIndexLayerLabel() || selectedIndexLayer || ''">
          <span class="opacity-icon">
            <i class="pi pi-images"></i>
          </span>
          <input
            type="range"
            min="0"
            max="100"
            [(ngModel)]="indexLayerOpacity"
            (ngModelChange)="osmIndexOverlay.setIndexLayerOpacity($event)"
            class="opacity-slider">
          <span class="opacity-value">{{ indexLayerOpacity }}%</span>
        </div>
      }
    </div>
  }
  <!-- Map Layer Switcher Control (Bottom Left) -->
  <div class="map-layer-switcher-control flex flex-row gap-2 align-items-end">
    <div class="layer-switcher-container">
      <div class="base-btn-wrapper">
        <button
          class="layer-btn base-main-btn"
          [class.active]="true"
          [title]="displayBaseLayerLabel | translate"
          (click)="toggleBaseSelector()">
          <span>{{ displayBaseLayerLabel | translate }}</span>
          <i class="pi pi-chevron-up"></i>
        </button>
        <app-satellite-style-selector
          [styles]="baseLayerMenuItems"
          [selected]="currentBaseLayer"
          [visible]="baseSelectorVisible"
          [headerLabel]="'Select base map'"
          (select)="onBaseLayerSelect($event)"
          (close)="baseSelectorVisible = false"
        ></app-satellite-style-selector>
      </div>

      <div class="satellite-btn-wrapper">
        <button
          class="layer-btn satellite-main-btn"
          [class.active]="hasActiveSatelliteLayer"
          [title]="displaySatelliteLabel"
          (click)="toggleSatelliteSelector()">
          <span>{{ displaySatelliteLabel }}</span>
          @if (hasActiveSatelliteLayer) {
            <span
              class="layer-clear-btn"
              role="button"
              [attr.title]="'Clear' | translate"
              (click)="clearActiveSatelliteLayer($event)">
              <i class="pi pi-times"></i>
            </span>
          }
          <i class="pi pi-chevron-up"></i>
        </button>
        <app-satellite-style-selector
          [styles]="satelliteMenuItems"
          [selected]="satelliteMenuSelection"
          [selectedIndex]="selectedIndexLayer"
          [visible]="satelliteSelectorVisible"
          [headerLabel]="'Select satellite source'"
          [dropDirection]="'up'"
          [enableIndexSearch]="true"
          (select)="onSatelliteMenuSelect($event)"
          (close)="satelliteSelectorVisible = false"
        ></app-satellite-style-selector>
      </div>
    </div>

    @if (showTimelineControls) {
      @if (showTimelineCalendar) {
      <div class="calendar-control-button">
        <app-button
          type="button"
          variant="secondary"
          size="large"
          class="map-toolbar-btn map-toolbar-btn--calendar"
          [title]="'Select date' | translate"
          (onClick)="openTimelineCalendar()">
          <img
            class="map-toolbar-btn__icon"
            src="/assets/icons/main/light/main-calendar-icon.svg"
            width="20"
            height="20"
            alt="">
        </app-button>
        <app-date-picker
          #timelineCalendar
          [hideTrigger]="true"
          [selectionMode]="'range'"
          [ngModel]="calendarDateRange"
          [selectableDates]="timelineSelectableDates"
          [allowOnlySelectedDate]="true"
          (ngModelChange)="onCalendarRangeChange($event)"
          (onYearChange)="onCalendarYearChange($event)">
        </app-date-picker>
      </div>
      }

      <div class="timeline-control">
        <app-satellite-timeline
          [dates]="timelineDates"
          [rangeStart]="timelineRangeStart"
          [rangeEnd]="timelineRangeEnd"
          [loading]="timelineLoading"
          [lang]="(translateCurrentLang)"
          [showAllButton]="showTimelineAllButton"
          [allModeActive]="satelliteDisplayMode === 'all'"
          (dateSelect)="onTimelineDateSelect($event)"
          (allSelect)="onTimelineAllSelect()"
          (reachStart)="onTimelineReachStart()"
          (reachEnd)="onTimelineReachEnd()"
        ></app-satellite-timeline>
      </div>
    }
  </div>

  <div class="index-panel-wrapper" *ngIf="showIndexPanel">
    <div class="index-panel">
      <msf-select
        [(model)]="selectedIndexLayer"
        [options]="indexLayerOptions"
        optionLabel="names"
        optionValue="value"
        [width]="'209px'"
        [placeholder]="'Select layer' | translate"
        (modelChange)="osmIndexOverlay.onIndexDateChange()"
        [showClear]="true"
      ></msf-select>

      <app-date-picker
        [style.width]="'209px'"
        [(ngModel)]="selectedIndexDate"
        [placeholder]="'Select date' | translate"
        [selectableDates]="indexAvailableDates"
        [allowOnlySelectedDate]="true"
        (ngModelChange)="osmIndexOverlay.onIndexDateChange()"
        (onYearChange)="osmIndexOverlay.getAvailableIndexDates($event)"
      ></app-date-picker>
    </div>
  </div>

  <div
    class="legend-floating"
    [class.collapsed]="legendCollapsed"
    [class.with-cursor-coordinates]="currentCursorLat !== null && currentCursorLng !== null">
    <div class="legend-spacer"></div>
    @if (currentLegend && osmIndexOverlay.hasVisibleIndexLayer() && !legendCollapsed) {
      <div class="map-legend-control inline">
        <button class="legend-collapse-btn" (click)="onIndexLegendClose()"
                [attr.aria-label]="'Collapse legend' | translate">
          <i class="pi pi-times"></i>
        </button>
        <div class="legend-header">
          <span class="legend-title">{{ osmIndexOverlay.getLegendTitle() }} {{ 'Index' | translate }}</span>
        </div>
        <div class="legend-items">
          @for (item of currentLegend; track item) {
            <div class="legend-item">
              <div class="color-box" [style.background]="item.color"></div>
              <div class="legend-info">
                <span class="range">{{ item.range }}</span>
                @if (item.descriptionKey) {
                  <span class="description">{{ item.descriptionKey | translate }}</span>
                }
              </div>
            </div>
          }
        </div>
      </div>
    } @else if (currentLegend && osmIndexOverlay.hasVisibleIndexLayer() && legendCollapsed) {
      <button class="legend-compact" (click)="osmIndexOverlay.toggleLegend()" [title]="'Show legend' | translate">
        <i class="pi pi-list"></i>
      </button>
    }
  </div>


  <!-- Cursor Coordinates Display (Top Center) -->
  @if (currentCursorLat !== null && currentCursorLng !== null) {
    <div class="map-cursor-coordinates"
         (click)="openGoToCoordinatesModal()">
      <div class="map-cursor-coordinates__icon">
        <i class="pi pi-search"></i>
      </div>
      <div class="map-cursor-coordinates__coords">
        <div class="map-cursor-coordinates__coord-lat">
          {{ 'Lat' | translate }}: {{ currentCursorLat | number:'1.6-6' }}
        </div>
        <div class="map-cursor-coordinates__coord-lng">
          <span class="map-cursor-coordinates__coord-label">{{ 'Lng' | translate }}:</span>
          <span class="map-cursor-coordinates__coord-value">{{ currentCursorLng | number:'1.6-6' }}</span>
        </div>
      </div>
    </div>
  }

  <app-go-to-coordinates-modal
    [(visible)]="goToCoordinatesModalVisible"
    [mapInstance]="mapInstance"
  ></app-go-to-coordinates-modal>

  <p-dialog
    [(visible)]="showIndexModal"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: '520px' }"
    [header]="'INDEX_TILE_MAP.INDEX' | translate"
    (onHide)="osmIndexOverlay.closeIndexModal()">

    <div class="index-modal-form">
      <msf-select
        class="index-select"
        [(model)]="selectedIndexLayer"
        [options]="indexLayerOptions"
        optionLabel="names"
        optionValue="value"
        [placeholder]="'Select layer' | translate"
        [showClear]="true"
        (modelChange)="osmIndexOverlay.onLayerChange($event)">
      </msf-select>

      <app-date-picker
        class="index-date"
        [(ngModel)]="selectedIndexDate"
        [placeholder]="'Select date' | translate"
        [selectableDates]="indexAvailableDates ?? null"
        [allowOnlySelectedDate]="true"
        (ngModelChange)="osmIndexOverlay.onDateChange($event)"
        (onYearChange)="osmIndexOverlay.getAvailableIndexDates($event)">
      </app-date-picker>

      <div class="index-modal-actions">
        <button
          class="display-btn"
          pButton
          type="button"
          label="{{ 'Show' | translate }}"
          [disabled]="!(selectedIndexLayer && selectedIndexDate)"
          (click)="osmIndexOverlay.applyIndexSelection()">
        </button>
      </div>
    </div>
  </p-dialog>

  @if (geoFileUploadModalVisible) {
    <!-- Geo File Upload Modal -->
    <p-dialog
      [(visible)]="geoFileUploadModalVisible"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '500px' }"
      [header]="'Upload Geo File' | translate"
      (onHide)="osmIndexOverlay.closeGeoFileUploadModal()">

      <!-- File Type Selection -->
      @if (!geoFileUploadType) {
        <div class="geo-file-type-selection flex flex-column gap-3">
          <p class="text-center">{{ 'Select file type to upload' | translate }}</p>
          <div class="flex justify-content-center gap-3">
            <button
              class="p-button p-button-outlined"
              (click)="osmIndexOverlay.selectGeoFileType('geojson')"
              style="min-width: 150px;">
              <i class="pi pi-file mr-2"></i>
              GeoJSON
            </button>
            <button
              class="p-button p-button-outlined"
              (click)="osmIndexOverlay.selectGeoFileType('kml')"
              style="min-width: 150px;">
              <i class="pi pi-map mr-2"></i>
              KML
            </button>
          </div>
        </div>
      } @else {
        <!-- File Upload -->
        <div class="geo-file-upload-section">
          <div class="flex align-items-center mb-3">
            <button class="p-button p-button-text p-button-sm" (click)="geoFileUploadType = null">
              <i class="pi pi-arrow-left mr-1"></i>
              {{ 'Back' | translate }}
            </button>
            <span class="ml-2 font-semibold">
              {{ (geoFileUploadType === 'gpkg' ? 'GeoPackage' : geoFileUploadType === 'kml' ? 'KML' : 'GeoJSON') }}
            </span>
          </div>

          <!-- 30 MB -->
          <app-file-upload
            [title]="(geoFileUploadType === 'gpkg' ? 'Select GeoPackage file (.gpkg)' : geoFileUploadType === 'kml' ? 'Select KML file (.kml)' : 'Select GeoJSON file') | translate"
            [multiple]="false"
            [accept]="geoFileUploadAccept"
            [maxFileSize]="300 * 1024 * 1024"
            (uploadComplete)="osmIndexOverlay.onGeoFileSelected($event)">
          </app-file-upload>
        </div>
      }

      @if (isGeoFileLoading) {
        <div class="loading-indicator flex align-items-center justify-content-center mt-3">
          <div class="loading-spinner-small"></div>
          <span class="ml-2">{{ 'Loading file...' | translate }}</span>
        </div>
      }
    </p-dialog>
  }


  <!-- Loaded Geo Layers List (shown when layers are loaded) -->
  @if (loadedGeoFileLayers.length > 0) {
    <div class="geo-layers-panel">
      <div class="geo-layers-header">
        <span>{{ 'Loaded Layers' | translate }}</span>
        <button class="clear-all-btn" (click)="osmIndexOverlay.removeAllGeoFileLayers()" [title]="'Remove all' | translate">
          <i class="pi pi-trash"></i>
        </button>
      </div>
      <div class="geo-layers-list">
        @for (layer of loadedGeoFileLayers; track layer.id) {
          <div class="geo-layer-item">
            <span class="layer-icon">
              @if (layer.type === 'gpkg') {
                <i class="pi pi-database"></i>
              } @else if (layer.type === 'kml') {
                <i class="pi pi-map"></i>
              } @else {
                <i class="pi pi-file"></i>
              }
            </span>
            <span class="layer-name" [title]="layer.name">{{ layer.name }}</span>
            <span class="feature-count">({{ layer.featureCount }})</span>
            <button class="remove-layer-btn" (click)="osmIndexOverlay.removeGeoFileLayer(layer.id)" [title]="'Remove' | translate">
              <i class="pi pi-times"></i>
            </button>
          </div>
        }
      </div>
    </div>
  }
</div>
